import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, CheckCircle, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Lien invalide');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lien invalide ou expire');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-bold text-[#111827] mb-2" style={{ fontFamily: 'Manrope' }}>
          Nouveau mot de passe
        </h1>

        {error && !success ? (
          <div className="text-center mt-8" data-testid="reset-error">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-gray-700 font-medium mb-2">{error}</p>
            <Link to="/forgot-password">
              <Button variant="outline" className="rounded-full mt-4" data-testid="reset-retry-btn">Faire une nouvelle demande</Button>
            </Link>
          </div>
        ) : success ? (
          <div className="text-center mt-8" data-testid="reset-success">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-violet-600" />
            </div>
            <h2 className="text-lg font-semibold mb-2">Mot de passe modifie !</h2>
            <p className="text-gray-500 text-sm mb-6">
              Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>
            <Link to="/admin/login">
              <Button className="bg-[#111827] hover:bg-gray-800 rounded-full" data-testid="reset-login-btn">Se connecter</Button>
            </Link>
          </div>
        ) : (
          <>
            <p className="text-gray-500 text-sm mb-6">
              Choisissez un nouveau mot de passe pour votre compte.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password">Nouveau mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pl-9"
                    placeholder="Min. 6 caracteres"
                    data-testid="reset-password-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="confirm">Confirmer le mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="pl-9"
                    placeholder="Confirmer"
                    data-testid="reset-confirm-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#111827] hover:bg-gray-800 rounded-full py-5" data-testid="reset-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Modifier le mot de passe
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
